import { t } from "i18next";
import { useEffect, useState } from "react";
import { useMediaQuery } from "react-responsive";
import styled from "styled-components";
import colors from "../../../theme/colors";
import AttachmentIcon from "../Icons/AttachmentIcon";
import DeleteIcon from "../Icons/DeleteIcon";
import ExitModalIcon from "../Icons/ExitModalIcon";
import UploadIcon from "../Icons/UploadIcon";
import ViewIcon from "../Icons/ViewIcon";

const FILE_ERRORS = {
  NONE: "",
  SIZE: "size",
  FORMAT: "format",
  DIMENSION: "dimension",
};

interface IUploadComponents {
  maxImageWidth?: number;
  maxImageHeight?: number;
  fileImageUrl: string;
  setBanner: any;
  inputId: string;
  idForUploadButton: string;
  styleForInputLabelContainer?: any;
  styleForUploadedContainer?: any;
  styleForFileRequirementContainer?: any;
  haveMarginLeft?: boolean;
  isDesktop?: boolean;
  type: string;
  index: number;
  setFileImageUrl?: any;
  banner?: any;
  onDeleteBanner: any;
}

interface IFileRequirement {
  uploadedFileErrorType?: string;
}

interface IFileRequirementContainer {
  haveMarginLeft?: boolean;
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 70%;
  margin-right: 80px;
`;

const Text = styled.div`
  font-size: 13px;
  font-weight: 600;
  line-height: 20px;
  color: ${(props) => props.theme.colors.body};
  margin-right: 16px;
`;

const FileNameContainer = styled.div`
  display: flex;
  align-items: center;
`;

const FileName = styled.p`
  color: ${(props) => props.theme.colors.label};
  word-break: break-all;
  margin: 0;

  @media screen and (max-width: 590px) {
    word-break: normal;
  }
`;

const IconsUploadContainer = styled.div`
  display: flex;
  margin-top: 20px;
`;

const FileRequirementContainer = styled.div<IFileRequirementContainer>`
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-left: ${(props) => (props.haveMarginLeft ? "22px" : "none")};
`;

const FileRequirement = styled.div<IFileRequirement>`
  display: flex;
  color: ${(props) =>
    props.uploadedFileErrorType !== FILE_ERRORS.NONE ? colors.error : colors.black};
  white-space: pre-wrap;
  width: 100%;
`;

const UploadedFileRequirementContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 22px;
  width: 100%;
`;

const UploadedFileRequirement = styled.div<IFileRequirement>`
  display: flex;
  color: ${(props) =>
    props.uploadedFileErrorType !== FILE_ERRORS.NONE ? colors.error : colors.black};
  width: 100%;
`;

const InputLabelFileContent = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  vertical-align: top;
`;

const LabelFile = styled.label`
  display: flex;
  align-items: center;
  text-align: center;
  justify-content: center;
  padding: 10px;
  font-size: 14px;
  font-weight: 700;
  color: ${(props) => props.theme.colors.body};
  cursor: pointer;
  border: 1px solid ${(props) => props.theme.colors.border};
  border-radius: 40px;
  width: 158px;
  height: 46px;

  @media only screen and (max-width: 1050px) {
    padding: 8px;
    font-size: 12px;
    width: 126px;
    height: 36px;
  }
`;

const InputFile = styled.input.attrs({ type: "file" })`
  display: none;
`;

const TitleAndIconsContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

export const Line = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 16px;
`;


const ModalWrapper = styled.div`
  width: 100%;
  height: 100%;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 100;
  display: flex;
  justify-content: center;
  padding: 0 20px;
  background-color: rgba(8, 8, 17, 0.8);
  cursor: pointer;
`;

const ModalContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 90%;
  height: 50%;
  margin-top: 42px;
`;

const Imagem = styled.img`
  align-self: center;
  max-width: 100%;
  height: auto;
  object-fit: contain;
`;

const UploadBanner: React.FC<IUploadComponents> = ({
  maxImageWidth,
  maxImageHeight,
  setBanner,
  inputId,
  fileImageUrl,
  idForUploadButton,
  styleForInputLabelContainer,
  haveMarginLeft,
  styleForUploadedContainer,
  styleForFileRequirementContainer,
  isDesktop,
  type,
  index,
  banner,
  onDeleteBanner,
}) => {
  const imageDimension = t('dashboard.image.dimensions', {maxImageWidth: maxImageWidth, maxImageHeight: maxImageHeight});
  const imageFormat = t('dashboard.image.format');
  const messageImage = imageDimension + '\n' + imageFormat;

  const [uploadedFileErrorType, setUploadedFileErrorType] = useState<string>(
    FILE_ERRORS.NONE
  );
  const [errorMessage, setErrorMessage] = useState<string>(messageImage);

  const [showModal, setShowModal] = useState(false);
  const [imageUrlToModal, setImageUrlToModal] = useState(fileImageUrl);

  const isSmallScreen = useMediaQuery({ query: "(max-width: 1050px)" });

  let uploadedImageDimensions = {};
  let uploadedFileError = FILE_ERRORS.NONE;
  let isFileUploadFailed = false;
  
  const checkFile = (file: File) => {
    const acceptedTypes = ["svg", "png", "jpg", "jpeg"];
    const fileType = file.type.replace("image/", "");
    if (!acceptedTypes.includes(fileType)) {
      uploadedFileError = FILE_ERRORS.FORMAT;
      setUploadedFileErrorType(FILE_ERRORS.FORMAT);
      isFileUploadFailed = true;
    }
  };

  const checkFileSize = (file: File) => {
    const FILE_SIZE_IN_BYTES = 10000000;
    if (file.size > FILE_SIZE_IN_BYTES) {
      uploadedFileError = FILE_ERRORS.SIZE;
      setUploadedFileErrorType(FILE_ERRORS.SIZE);
      isFileUploadFailed = true;
    }
  };

  const getImageDimensions = async (fileInput: any) => {
    const oFReader = new FileReader();
    oFReader.readAsDataURL(fileInput);
    oFReader.onload = async (event: any) => {
      let image = new Image();
      image.src = event.target.result;
      image.onload = function () {
        uploadedImageDimensions = { width: image.width, height: image.height };
      };
    };
  };

  const checkFileDimension = (fileDimension: any) => {
    if (
      (maxImageWidth && fileDimension.width > maxImageWidth) ||
      (maxImageHeight && fileDimension.height > maxImageHeight)
    ) {
      uploadedFileError = FILE_ERRORS.DIMENSION;
      setUploadedFileErrorType(FILE_ERRORS.DIMENSION);
      isFileUploadFailed = true;
    }
  };

  const checkImageErrorMessage = (uploadedFileError: any) => {
    switch (uploadedFileError) {
      case FILE_ERRORS.FORMAT:
        setErrorMessage(
          t('dashboard.image.error.format')!
        );
        return;
      case FILE_ERRORS.SIZE:
        setErrorMessage( 
          t('dashboard.image.error.size')!
        );
        return;
      case FILE_ERRORS.DIMENSION:
        setErrorMessage(
          t('dashboard.image.error.dimensions', {maxImageWidth: maxImageWidth, maxImageHeight: maxImageHeight})!
        );
        return;
      case FILE_ERRORS.NONE:
        setErrorMessage(messageImage);
        return;
      default:
        return;
    }
  };
  const handleLoadFile = async (event: any) => {
    const [file]: File[] = event.target.files;

    checkFile(file);
    checkFileSize(file);
    getImageDimensions(file);
    await new Promise((resolve) => setTimeout(resolve, 900));
    checkFileDimension(uploadedImageDimensions);

    if (isFileUploadFailed) {
      checkImageErrorMessage(uploadedFileError);
      setBanner((prev: any) => {
        const newBanners = Array.isArray(prev) ? [...prev] : [];
        newBanners[index] = {
          ...newBanners[index],
          [`${type}_url`]: "",
          [`${type}_file`]: "",
          [`${type}_name`]: "",
        };
        return newBanners;
      });
    } else {
      uploadedFileError = FILE_ERRORS.NONE;
      const url = URL.createObjectURL(file);
      setBanner((prev: any) => {
        const newBanners = Array.isArray(prev) ? [...prev] : [];
        newBanners[index] = {
          ...newBanners[index],
          [`${type}_url`]: url,
          [`${type}_file`]: file,
          [`${type}_name`]: file.name,
        };
        return newBanners;
      });
      setUploadedFileErrorType(FILE_ERRORS.NONE);
      checkImageErrorMessage(uploadedFileError);
    }
  };

  const handleImagePreview = () => {
    setShowModal(true);
    setImageUrlToModal(banner[type + "_url"]);
  };

  const handleRemoveFile = () => {
    onDeleteBanner(index, type);
    uploadedFileError = FILE_ERRORS.NONE;
    setBanner((prev: any) => {
      const newBanners = Array.isArray(prev) ? [...prev] : [];
      newBanners[index] = {
        ...newBanners[index],
        [`${type}_url`]: "",
        [`${type}_file`]: "",
        [`${type}_name`]: "",
      };
      return newBanners;
    });
    setErrorMessage("");
    setUploadedFileErrorType(FILE_ERRORS.NONE);
    checkImageErrorMessage(uploadedFileError);
  };

  return (
    <>
      {!banner[`${type}_name`] ? (
        <InputLabelFileContent style={styleForInputLabelContainer}>
          <Line>
            <Text>{isDesktop ? "Desktop" : "Mobile"}</Text>
            <LabelFile className="label" htmlFor={inputId}>
              {isSmallScreen ? t('dashboard.image.add') : t('dashboard.image.addImage')}
            </LabelFile>
          </Line>
          <br />
          <InputFile
            type="file"
            id={inputId}
            onChange={(event) => handleLoadFile(event)}
          />
           <UploadedFileRequirementContainer>
            {(maxImageWidth && maxImageHeight) && (
              <UploadedFileRequirement
                uploadedFileErrorType={uploadedFileErrorType}
                style={{ color: colors.body }}
              >
                {imageDimension}
              </UploadedFileRequirement>
            )}
            <UploadedFileRequirement
              uploadedFileErrorType={uploadedFileErrorType}
              style={{ color: colors.body }}
            >
              {imageFormat}
            </UploadedFileRequirement>
          </UploadedFileRequirementContainer>
        </InputLabelFileContent>
      ) : (
        <Container style={styleForUploadedContainer}>
          <TitleAndIconsContainer>
            <FileNameContainer>
              <AttachmentIcon style={{ marginRight: "8px" }} />
              <FileName>{banner[`${type}_name`]}</FileName>
            </FileNameContainer>
            <IconsUploadContainer>
              <ViewIcon
                onClick={handleImagePreview}
                style={{ marginRight: "24px" }}
              />
              <UploadIcon
                handleLoadFile={handleLoadFile}
                idForUploadButton={idForUploadButton}
              />
              <DeleteIcon
                style={{ marginLeft: "24px" }}
                handleRemoveFile={handleRemoveFile}
              />
            </IconsUploadContainer>
          </TitleAndIconsContainer>
          <UploadedFileRequirementContainer>
            {(maxImageWidth && maxImageHeight) && (
              <UploadedFileRequirement
                uploadedFileErrorType={uploadedFileErrorType}
                style={{ color: colors.body }}
              >
                {imageDimension}
              </UploadedFileRequirement>
            )}
            <UploadedFileRequirement
              uploadedFileErrorType={uploadedFileErrorType}
              style={{ color: colors.body }}
            >
              {imageFormat}
            </UploadedFileRequirement>
          </UploadedFileRequirementContainer>
          {showModal && (
            <ModalWrapper>
              <ModalContainer>
                <ExitModalIcon
                  previewImageModal
                  onClick={() => setShowModal(false)}
                  style={{ alignSelf: "end" }}
                />
                <Imagem src={imageUrlToModal} alt="banner preview" id="imageId" />
              </ModalContainer>
            </ModalWrapper>
          )}
        </Container>
      )}
    </>
  );
};

export default UploadBanner;
